import { ThreadListPrimitive, useAuiState } from '@assistant-ui/react';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import ButtonBase from '@mui/material/ButtonBase';
import Tooltip from '@mui/material/Tooltip';
import FocusTrap from '@mui/material/Unstable_TrapFocus';
import clsx from 'clsx';
import { useRef, useState } from 'react';

import { AgentWorkbenchNotificationToggle } from './agent-workbench-notification-toggle.js';
import { AgentWorkbenchThreadList } from './agent-workbench-thread-list.js';

import type { ReactNode } from 'react';

interface AgentWorkbenchThreadHistoryProps {
	onSelectThread?: (conversationId: string) => void;
	onNewThread?: () => void;
	allowThreadCreation?: boolean;
	allowThreadDeletion?: boolean;
	/** Optional element rendered after the conversation title, before the notification toggle. */
	accessory?: ReactNode;
}

/**
 * Conversation-history affordance for the workbench panel: a slim toolbar (history
 * toggle, active conversation title, quick new-conversation) plus a slide-over
 * drawer holding the full {@link AgentWorkbenchThreadList}. The drawer is positioned
 * within the workbench panel, so the panel root must be `relative`; focus is trapped
 * while open and restored to the toggle on close. Must render inside an
 * `AssistantRuntimeProvider`.
 */
export function AgentWorkbenchThreadHistory({
	onSelectThread,
	onNewThread,
	allowThreadCreation = true,
	allowThreadDeletion = true,
	accessory,
}: AgentWorkbenchThreadHistoryProps) {
	const [open, setOpen] = useState(false);
	const toggleRef = useRef<HTMLButtonElement>(null);
	const activeTitle = useAuiState((s) => s.threadListItem.title);

	const close = (): void => {
		setOpen(false);
		toggleRef.current?.focus();
	};
	// The drawer dismisses on every activation; the caller is only notified once
	// the row's conversation has a server-assigned id.
	const selectThread = (conversationId: string | undefined): void => {
		close();
		if (conversationId !== undefined) {
			onSelectThread?.(conversationId);
		}
	};

	return (
		<>
			<div className='flex shrink-0 items-center gap-2 border-b border-gray-a6 bg-gray-2 px-2 py-1.5'>
				<Tooltip title='Conversation history'>
					<ButtonBase
						ref={toggleRef}
						onClick={() => setOpen(true)}
						aria-haspopup='dialog'
						aria-expanded={open}
						aria-label='Conversation history'
						className='flex items-center justify-center rounded p-1 text-gray-11 transition-colors duration-150 hover:bg-gray-a3 hover:text-gray-12 motion-reduce:transition-none'
					>
						<HistoryIcon style={{ fontSize: 18 }} />
					</ButtonBase>
				</Tooltip>

				<span className='truncate min-w-0 flex-1 text-gray-12'>{activeTitle ?? 'New conversation'}</span>

				{accessory}

				<AgentWorkbenchNotificationToggle />

				{allowThreadCreation && (
					<Tooltip title='New conversation'>
						<ThreadListPrimitive.New asChild onClick={onNewThread}>
							<ButtonBase
								aria-label='New conversation'
								className='flex items-center justify-center rounded p-1 text-blue-11 transition-colors duration-150 hover:bg-blue-a3 motion-reduce:transition-none'
							>
								<AddIcon style={{ fontSize: 18 }} />
							</ButtonBase>
						</ThreadListPrimitive.New>
					</Tooltip>
				)}
			</div>

			<div
				className={clsx(
					'absolute inset-0 z-10 transition-opacity duration-200 motion-reduce:transition-none',
					open ? 'opacity-100' : 'pointer-events-none opacity-0',
				)}
			>
				<button
					type='button'
					aria-hidden
					tabIndex={-1}
					onClick={close}
					className='absolute inset-0 h-full w-full cursor-default bg-gray-1/70 backdrop-blur-sm'
				/>
				<FocusTrap open={open} disableRestoreFocus>
					<div
						role='dialog'
						aria-modal
						aria-label='Conversation history'
						tabIndex={-1}
						inert={!open}
						onKeyDown={(e) => {
							if (e.key === 'Escape') {
								close();
							}
						}}
						className={clsx(
							'absolute inset-y-0 left-0 flex w-72 max-w-[85%] flex-col border-r border-gray-6 outline-none',
							'transition-transform duration-200 ease-out motion-reduce:transition-none',
							open ? 'translate-x-0' : '-translate-x-full',
						)}
					>
						<AgentWorkbenchThreadList
							onSelectThread={selectThread}
							onNewThread={() => {
								close();
								onNewThread?.();
							}}
							allowThreadCreation={allowThreadCreation}
							allowThreadDeletion={allowThreadDeletion}
						/>
					</div>
				</FocusTrap>
			</div>
		</>
	);
}
